import { orchestrator, mutatorAction } from 'satcheljs';
import {
    ComposeViewState,
    ComposeLifecycleEvent,
    PostOpenTask,
    PostOpenTaskType,
    InitAttachmentsTask,
    SaveAndUpgradeTask,
    AddInfoBarTask,
    SendTask,
    CloseConflictComposeTask,
} from 'owa-mail-compose-store';
import onComposeLifecycleEvent from 'owa-mail-compose-store/lib/actions/onComposeLifecycleEvent';
import { initializeQuickUseDataLazy } from 'owa-quickuse';
import trySendMessage from '../actions/trySendMessage';
import upgradeCompose from '../actions/upgradeCompose';
import loadComplianceConfig from 'owa-mail-protection/lib/actions/loadComplianceConfig';
import loadItemLabelBeforeCompose from '../utils/loadItemLabelBeforeCompose';
import initAttachments from '../actions/initAttachments';
import preload from '../utils/preload';
import loadComposeInfoBars from '../utils/loadComposeInfoBars';
import insertSignature from '../utils/insertSignature';
import notifyOpxReady from '../utils/notifyOpxReady';
import closeConflictCompose from '../utils/closeConflictCompose';

const taskHandlerMap: Record<
    PostOpenTaskType,
    (viewState: ComposeViewState, task: PostOpenTask) => void | Promise<void>
> = {
    // Ideally we can directly put most functions as value here, but that will make spyOn in test case not working.
    // So we add one more wrapper for each function here.
    [PostOpenTaskType.CloseConflictCompose]: (viewState, task) =>
        closeConflictCompose(task as CloseConflictComposeTask),
    [PostOpenTaskType.Preload]: viewState => preload(viewState),
    [PostOpenTaskType.InsertSignature]: viewState =>
        insertSignature(viewState, true /*isAutoSignature*/),
    [PostOpenTaskType.InitAttachments]: (viewState, task) =>
        initAttachments(viewState, task as InitAttachmentsTask),
    [PostOpenTaskType.SaveAndUpgrade]: (viewState, task) =>
        upgradeCompose(viewState, task as SaveAndUpgradeTask),
    [PostOpenTaskType.LoadComplianceConfig]: () => loadComplianceConfig(),
    [PostOpenTaskType.LoadItemLabel]: viewState => loadItemLabelBeforeCompose(viewState),
    [PostOpenTaskType.AddInfoBar]: (viewState, task) =>
        loadComposeInfoBars(viewState, task as AddInfoBarTask),
    // TODO: 78271 [Projection] Support Addin popout and send in projection
    [PostOpenTaskType.Send]: (viewState, task) =>
        trySendMessage(
            viewState,
            window,
            (task as SendTask).bypassClientSideValidation
        ).catch(e => {}),
    [PostOpenTaskType.QuickUse]: () => initializeQuickUseDataLazy.importAndExecute(),
    [PostOpenTaskType.OpxNotifyReady]: () => notifyOpxReady(),
};

const setPostOpenTaskExecuted = mutatorAction('setPostOpenTaskExecuted', (task: PostOpenTask) => {
    task.executed = true;
});

export async function postOpenOrchestrator(actionMessage: {
    viewState: ComposeViewState;
    event: ComposeLifecycleEvent;
}) {
    const { viewState, event } = actionMessage;

    if (event == ComposeLifecycleEvent.Opened) {
        const validTasks = viewState.postOpenTasks.filter(task => !task.executed);
        for (let i = 0; i < validTasks.length; i++) {
            const task = validTasks[i];
            await taskHandlerMap[task.type](viewState, task);
            setPostOpenTaskExecuted(task);
        }
    }
}

export default orchestrator(onComposeLifecycleEvent, postOpenOrchestrator);
